import { create } from "zustand";
import { devtools } from "zustand/middleware";
import {
  getAlerts,
  createAlert as apiCreateAlert,
  updateAlert as apiUpdateAlert,
  toggleAlertStatus as apiToggleAlertStatus,
  deleteAlert as apiDeleteAlert,
} from "../setup/api/services/alerts";
import {
  IAlert,
  EAlertStatus,
  ICreateAlertBody,
  IUpdateAlertBody,
} from "../setup/api/services/alerts/models";

interface FetchParams {
  page?: number;
  size?: number;
}

interface AlertsState {
  alerts: IAlert[];
  loading: boolean;
  error: string | null;
  fetchAlerts: (params?: FetchParams) => Promise<void>;
  createAlert: (alertData: ICreateAlertBody) => Promise<IAlert | null>;
  updateAlert: (
    id: string,
    alertData: IUpdateAlertBody
  ) => Promise<IAlert | null>;
  toggleAlertStatus: (id: string) => Promise<IAlert | void>;
  deleteAlert: (id: string) => Promise<void>;
  setAlerts: (alerts: IAlert[]) => void;
}

export const useAlertsStore = create<AlertsState>()(
  devtools((set, get) => ({
    alerts: [],
    loading: false,
    error: null,

    setAlerts: (alerts) => set({ alerts }),

    createAlert: async (alertData: ICreateAlertBody) => {
      try {
        set({ loading: true, error: null });
        const newAlert = await apiCreateAlert(alertData);
        set({ alerts: [newAlert, ...get().alerts] });
        return newAlert;
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Error creating alert:", err);
        set({ error: "Failed to create alert" });
        return null;
      } finally {
        set({ loading: false });
      }
    },

    updateAlert: async (id: string, alertData: IUpdateAlertBody) => {
      try {
        set({ loading: true, error: null });
        const updatedAlert = await apiUpdateAlert(id, alertData);
        set({
          alerts: get().alerts.map((alert) =>
            alert.id === id ? updatedAlert : alert
          ),
        });
        return updatedAlert;
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Error updating alert:", err);
        set({ error: "Failed to update alert" });
        return null;
      } finally {
        set({ loading: false });
      }
    },

    fetchAlerts: async (params = { page: 0, size: 100 }) => {
      try {
        set({ loading: true, error: null });
        const response = await getAlerts(params);
        set({ alerts: response.data.alertWidgetDTOs || [] });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Error fetching alerts:", err);
        set({ error: "Failed to fetch alerts" });
      } finally {
        set({ loading: false });
      }
    },

    toggleAlertStatus: async (id: string) => {
      const { alerts } = get();
      const target = alerts.find((a) => a.id === id);
      if (!target) return;

      const newStatus =
        target.status === EAlertStatus.ACTIVE
          ? EAlertStatus.INACTIVE
          : EAlertStatus.ACTIVE;

      try {
        const updatedAlert = await apiToggleAlertStatus(id, newStatus);
        set({
          alerts: alerts.map((a) => (a.id === id ? updatedAlert : a)),
        });
        return updatedAlert;
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Error toggling alert status:", err);
        set({ error: "Failed to update alert status" });
        throw err;
      }
    },

    deleteAlert: async (id: string) => {
      try {
        await apiDeleteAlert(id);
        set({ alerts: get().alerts.filter((a) => a.id !== id) });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Error deleting alert:", err);
        set({ error: "Failed to delete alert" });
        throw err;
      }
    },
  }))
);
